import { Link } from '@tanstack/react-router'
import { Check, LogOut, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useLogout } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'

type OnboardingStep = 1 | 2

/**
 * The full-screen frame of the setup. During the first setup it shows the steps and a way to sign
 * out instead of the way back to the dashboard.
 */
export function SetupFrame({
  back,
  onboardingStep,
  children,
}: {
  back?: ReactNode
  onboardingStep?: OnboardingStep
  children: ReactNode
}) {
  return (
    <div className="beacon-canvas flex min-h-dvh flex-col">
      <header className="flex h-16 shrink-0 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <img src="/pulsify-wordmark.svg" alt="Pulsify" className="h-7 w-auto" />
        {onboardingStep ? (
          <SignOutButton />
        ) : (
          <Link
            to="/dashboard"
            aria-label={m.setupProfileClose()}
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </Link>
        )}
      </header>
      <main className="beacon-enter flex-1 px-4 pb-10 sm:px-6 lg:px-8">
        {onboardingStep ? <OnboardingSteps current={onboardingStep} /> : null}
        {back ? <div className="mx-auto mb-4 w-full max-w-5xl">{back}</div> : null}
        {children}
      </main>
    </div>
  )
}

function SignOutButton() {
  const { mutate: logout, isPending } = useLogout()
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => logout()}
      disabled={isPending}
      className="text-muted-foreground"
    >
      <LogOut />
      {m.signOut()}
    </Button>
  )
}

function OnboardingSteps({ current }: { current: OnboardingStep }) {
  const steps = [m.onboardingStepProfile(), m.onboardingStepCompany()]
  return (
    <ol
      aria-label={m.onboardingStepsLabel()}
      className="mx-auto mb-6 flex w-full max-w-5xl items-center justify-center gap-3 pt-2 text-sm"
    >
      {steps.map((label, index) => {
        const step = index + 1
        const isCurrent = step === current
        const isDone = step < current
        return (
          <li key={label} className="flex items-center gap-3">
            {index > 0 ? (
              <span aria-hidden="true" className="text-muted-foreground">
                ·
              </span>
            ) : null}
            <span
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex items-center gap-2',
                isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-xs',
                  isCurrent ? 'bg-brand-subtle text-brand-subtle-foreground' : 'bg-muted',
                )}
              >
                {isDone ? <Check className="h-3 w-3" /> : step}
              </span>
              {label}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
